import * as yup from 'yup';
import { incomeTypeList, YesNoList, YesNo } from 'contexts/FormProvider';
import { expenseFormFieldsNumberKeys } from 'contexts/initialState';
import { validateDecimalSchema, validateTextSchema } from 'utils/commonSchema';

yup.setLocale({
  mixed: {
    oneOf: '',
    required: '',
  },
});

const textFieldMin = 1;
const textFieldMax = 225;
interface FileInfo {
  haveValidFileType: boolean;
  totalFileSize: number;
}

const endsWithAny = (suffixes: string[], name: string) =>
  suffixes.some((suffix) => name.toLowerCase().endsWith(`.${suffix}`));

export const incomeSchema = yup.array().when(['mode', 'incomeStatus'], {
  is: (mode: string, incomeStatus: YesNo) => mode === 'Edit' && incomeStatus === 'yes',
  then: () =>
    yup.array().of(
      yup.object().shape({
        id: yup.string(),
        incomeType: yup
          .string()
          .oneOf([...incomeTypeList])
          .required(),
        incomeDescription: validateTextSchema({ minLen: textFieldMin, maxLen: textFieldMax, required: true }),
        incomeAmount: validateDecimalSchema({
          errorMessage: 'Please enter a valid value',
          required: true,
        }),
      })
    ),
});

const salaryErrorMessage = 'Please enter a valid value or enter $0 where not relevant';

const expenseFieldSchema = yup.mixed().when('mode', {
  is: 'Expense',
  then: () =>
    yup
      .number()
      .min(1, salaryErrorMessage)
      .transform((_, v) => (v === '' ? null : +v.replace(/,/g, '')))
      .required(salaryErrorMessage)
      .moreThan(-1, salaryErrorMessage)
      .nonNullable(),
});

const expenseSchemaObject = expenseFormFieldsNumberKeys.reduce(
  (acc: object, curr: string) => ({
    ...acc,
    [curr]: expenseFieldSchema,
  }),
  {}
);

const incomeSchemaObject = {
  incomeStatus: yup.string().when('mode', {
    is: 'Edit',
    then: () =>
      yup
        .string()
        .oneOf([...YesNoList])
        .nullable()
        .required(),
  }),

  incomeData: incomeSchema,
};
export const incomeValidationSchema = yup.object().shape(incomeSchemaObject);

const validationSchema = yup.object().shape({
  ...incomeSchemaObject,
  ...expenseSchemaObject,
  declaration: yup.boolean().when('mode', {
    is: 'Expense',
    then: () => yup.boolean().oneOf([true]),
  }),

  files: yup
    .mixed()
    .nullable()
    .test('fileError', `file error`, function (files = []) {
      if (!Array.isArray(files)) {
        throw new Error('files should be an array');
      }

      const { maxFileSize, allowedFileType } = this.parent;
      const { createError } = this;

      const { totalFileSize, haveValidFileType } = files.reduce(
        (prev: FileInfo, current: File) => ({
          haveValidFileType: prev.haveValidFileType && endsWithAny(allowedFileType, current.name),
          totalFileSize: prev.totalFileSize + current.size,
        }),
        {
          haveValidFileType: true,
          totalFileSize: 0,
        }
      );
      const isValidSize = totalFileSize === 0 || totalFileSize <= maxFileSize * 1024 * 1024;
      if (!isValidSize) {
        return createError({
          message: `Maximum file size limit of ${maxFileSize}MB has been exceeded, please remove some files.`,
        });
      }
      if (!haveValidFileType) {
        return createError({
          message: 'This file type is not allowed, please attach a different document type.',
        });
      }

      return true;
    }),
});

export default validationSchema;
